import { useQuery } from "@tanstack/react-query";
import type { CategoriesResponse, Category, MeResponse } from "@workcost/shared";
import { apiJson } from "./api";
import { useAuth } from "./AuthContext";
import { isMockApi, mockCategories } from "./estimateApi";
import { mockMe } from "./jobsApi";

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async (): Promise<Category[]> => {
      const mocked = await mockCategories();
      if (mocked) return [...mocked].sort((a, b) => a.display_order - b.display_order);
      const res = await apiJson<CategoriesResponse>("/categories", { auth: false });
      return [...res.items].sort((a, b) => a.display_order - b.display_order);
    },
    staleTime: 60 * 60 * 1000,
  });
}

export function useMe() {
  const { status } = useAuth();
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => (await mockMe()) ?? apiJson<MeResponse>("/me"),
    enabled: status === "authenticated" || isMockApi(),
    staleTime: 60 * 1000,
  });
}

export const CATEGORY_ICON: Record<string, "brush" | "zap" | "droplet" | "bricks" | "grid" | "square" | "sun" | "trash"> = {
  PAINT: "brush",
  ELEC: "zap",
  PLUMB: "droplet",
  STRUCT_01: "bricks",
  CEIL_TBAR: "grid",
  CEIL_GYPS: "square",
  WALL_BRICK: "bricks",
  FLOOR_TILE: "grid",
  HVAC: "sun",
  WATERP: "droplet",
  DEMO: "trash",
};

export function categoryName(c: Pick<Category, "name_th" | "name_en">, lang: "th" | "en"): string {
  return lang === "en" ? c.name_en : c.name_th;
}

export function categoryFromPrice(c: Category): number {
  return c.labor_min_thb + c.material_min_thb;
}
